import { Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import { MenuOptions } from 'models';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider } from '@mui/material';

import { BurgerIcon, Header, StyledCloseIcon, StyledBadge } from './BurgerMenu.styles';

interface BurgerMenuProps {
  options: MenuOptions[];
  isVisible: boolean;
  totalVouchers: number;
  hasNewVouchers: boolean;
  onToggle: () => void;
  anchor?: 'top' | 'left' | 'bottom' | 'right';
}

interface MenuIconProps {
  showBadge: boolean;
  onToggle: () => void;
}

interface ListItemProps {
  hasNewVouchers: boolean;
  showBadge: boolean | undefined;
  label: string;
  badgeCount: number | undefined;
}

const MenuIcon = ({ showBadge, onToggle }: MenuIconProps) => {
  const NavIcon = () => <BurgerIcon onClick={onToggle} aria-label="Navigation Icon" />;

  if (showBadge) {
    return (
      <StyledBadge color="secondary" variant="dot" aria-label="New perk indicator">
        <NavIcon />
      </StyledBadge>
    );
  }

  return <NavIcon />;
};

const ListItemContent = ({ hasNewVouchers, showBadge, label, badgeCount }: ListItemProps) => {
  const ListItem = () => <ListItemText primary={label} />;

  if (showBadge) {
    const color = hasNewVouchers ? 'secondary' : 'primary';

    return (
      <StyledBadge color={color} badgeContent={badgeCount}>
        <ListItem />
      </StyledBadge>
    );
  }

  return <ListItem />;
};

function BurgerMenu({
  options,
  isVisible,
  onToggle,
  anchor = 'right',
  totalVouchers,
  hasNewVouchers,
}: BurgerMenuProps) {
  const history = useHistory();

  const handleOnClick = (option: MenuOptions) => {
    const { path, onClick } = option;

    if (onClick) {
      onClick();
      return;
    }

    history.push(path);
  };

  return (
    <>
      <MenuIcon showBadge={hasNewVouchers} onToggle={onToggle} />

      <Drawer aria-label="Burger Menu" anchor={anchor} open={isVisible} onClose={onToggle}>
        <List>
          <Header>
            <StyledCloseIcon onClick={onToggle} aria-label="Close menu" />
          </Header>
          <Divider />

          {options.map((option) => {
            const { path, label, icon: Icon, iconProps, showBadge } = option;
            const isActive = path === history.location.pathname;

            return (
              <Fragment key={label}>
                <ListItemButton onClick={() => handleOnClick(option)} selected={isActive}>
                  <ListItemIcon>
                    <Icon {...iconProps} />
                  </ListItemIcon>
                  <ListItemContent
                    hasNewVouchers={hasNewVouchers}
                    showBadge={showBadge}
                    label={label}
                    badgeCount={totalVouchers}
                  />
                </ListItemButton>
                <Divider />
              </Fragment>
            );
          })}
        </List>
      </Drawer>
    </>
  );
}

export default BurgerMenu;
